import React from 'react';

import { Alert, Box, Chip, Stack, Typography } from '@mui/material';

import type { CorridorOpportunity } from '~/types/corridorOpportunity';

import { aud, isPriceValidationFresh, providerName } from './formatters';

interface LeFareBenchmarkProps {
  opportunity: CorridorOpportunity;
  departureDate: string;
  returnDate: string;
  carrier: string;
}

export default function LeFareBenchmark({ opportunity, departureDate, returnDate, carrier }: LeFareBenchmarkProps) {
  const { paidFareP25Aud, paidFareMedianAud, paidFareP75Aud } = opportunity.evidence;
  const validationMatchesSelectedDates =
    opportunity.priceValidation?.departureDate === departureDate &&
    opportunity.priceValidation?.returnDate === returnDate &&
    opportunity.priceValidation?.carrier?.toUpperCase() === carrier.trim().toUpperCase();
  const livePrice = validationMatchesSelectedDates ? (opportunity.priceValidation?.perAdultPrice ?? null) : null;
  const liveLuxPlusPrice = validationMatchesSelectedDates
    ? (opportunity.priceValidation?.luxPlusPerAdultPrice ?? null)
    : null;
  const liveProvider = validationMatchesSelectedDates
    ? providerName(opportunity.priceValidation?.provider ?? '')
    : null;
  const livePriceIsFresh = validationMatchesSelectedDates && isPriceValidationFresh(opportunity);
  const hasBenchmark =
    paidFareP25Aud !== null &&
    paidFareMedianAud !== null &&
    paidFareP75Aud !== null &&
    paidFareP25Aud <= paidFareMedianAud &&
    paidFareMedianAud <= paidFareP75Aud;

  if (!hasBenchmark) {
    return (
      <Alert severity="info">
        There is not enough LE adult-only booking history to compare prices. Use the live supplier fare, margin and
        booking sample together rather than making a price-led campaign claim.
      </Alert>
    );
  }

  const p25 = paidFareP25Aud;
  const median = paidFareMedianAud;
  const p75 = paidFareP75Aud;
  const spread = Math.max(p75 - p25, 1);
  const scaleMin = Math.max(0, p25 - spread * 0.5);
  const scaleMax = p75 + spread * 0.5;
  const position = (value: number) => Math.min(100, Math.max(0, ((value - scaleMin) / (scaleMax - scaleMin)) * 100));
  const p25Position = position(p25);
  const medianPosition = position(median);
  const p75Position = position(p75);
  const livePosition = livePrice === null ? null : Math.min(94, Math.max(6, position(livePrice)));
  const historicalPriceBand =
    livePrice === null ? null : livePrice <= p25 ? 'low' : livePrice <= p75 ? 'typical' : 'high';
  const livePriceBand = livePriceIsFresh ? historicalPriceBand : null;

  const decision =
    livePrice !== null && !livePriceIsFresh
      ? {
          severity: 'warning' as const,
          title: 'Previous live fare has expired',
          detail: `The previous ${liveProvider} fare of ${aud(livePrice)} is shown for context only. Choose a date pair in the calendar grid; draft creation will run the final customer-price check.`,
        }
      : livePriceBand === 'low'
        ? {
            severity: 'success' as const,
            title: 'Strong current fare against LE history',
            detail: `The checked ${liveProvider} fare is at or below ${aud(p25)}, the lower benchmark from LE bookings. This supports a price-led campaign if margin and reliability are also acceptable.`,
          }
        : livePriceBand === 'typical'
          ? {
              severity: 'info' as const,
              title: 'Typical current fare against LE history',
              detail: `The checked ${liveProvider} fare sits inside the range where the middle half of LE adult-only bookings historically paid. It is not a standout price on this evidence alone, so the campaign needs another compelling reason.`,
            }
          : livePriceBand === 'high'
            ? {
                severity: 'warning' as const,
                title: 'High current fare against LE history',
                detail: `The checked ${liveProvider} fare is above ${aud(p75)}, the upper benchmark from LE bookings. Try different dates or a different carrier before creating a price-led campaign.`,
              }
            : null;

  return (
    <Stack gap={2}>
      <Box>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Historical LE paid amount per adult · last 90 days
          </Typography>
          <Chip size="small" variant="outlined" color="primary" label="LE booking & sales data" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Source: Luxury Escapes internal flight booking and sales records across all suppliers—not Sabre market-wide
          booking data. For adult-only return bookings, the paid amount is LE gross sales divided by the number of
          adults. Half of those bookings were between {aud(p25)} and {aud(p75)} per adult; the midpoint was{' '}
          {aud(median)}.
        </Typography>
      </Box>

      <Box
        role="img"
        aria-label={`Historical LE paid amount range from ${aud(p25)} to ${aud(p75)}, median ${aud(median)}${
          livePrice === null ? '' : `, checked ${liveProvider} fare ${aud(livePrice)}`
        }`}
        sx={{ px: { xs: 1, sm: 4 }, pt: livePrice === null ? 2 : 7 }}
      >
        <Box sx={{ height: 12, position: 'relative' }}>
          <Box
            sx={{
              bgcolor: 'success.light',
              borderRadius: '999px 0 0 999px',
              bottom: 0,
              left: 0,
              position: 'absolute',
              top: 0,
              width: `${p25Position}%`,
            }}
          />
          <Box
            sx={{
              bgcolor: 'primary.light',
              bottom: 0,
              left: `${p25Position}%`,
              position: 'absolute',
              top: 0,
              width: `${p75Position - p25Position}%`,
            }}
          />
          <Box
            sx={{
              bgcolor: 'warning.light',
              borderRadius: '0 999px 999px 0',
              bottom: 0,
              left: `${p75Position}%`,
              position: 'absolute',
              right: 0,
              top: 0,
            }}
          />
          <Box
            sx={{
              bgcolor: 'common.white',
              border: '2px solid',
              borderColor: 'primary.dark',
              borderRadius: '50%',
              height: 16,
              left: `${medianPosition}%`,
              position: 'absolute',
              top: -2,
              transform: 'translateX(-50%)',
              width: 16,
            }}
          />
          {livePrice !== null && livePosition !== null && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                left: `${livePosition}%`,
                position: 'absolute',
                top: -48,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                zIndex: 1,
              }}
            >
              <Chip
                size="small"
                color={
                  !livePriceIsFresh
                    ? 'default'
                    : historicalPriceBand === 'low'
                      ? 'success'
                      : historicalPriceBand === 'high'
                        ? 'warning'
                        : 'primary'
                }
                label={`${livePriceIsFresh ? 'Live' : 'Expired'} ${liveProvider} fare ${aud(livePrice)}${
                  liveLuxPlusPrice !== null ? ` · Lux Plus ${aud(liveLuxPlusPrice)}` : ''
                }`}
              />
              <Box sx={{ bgcolor: 'text.primary', height: 24, mt: 0.5, width: 2 }} />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            mt: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Lower LE benchmark · P25
            </Typography>
            <Typography fontWeight={700}>{aud(p25)}</Typography>
            <Typography variant="caption">25% of LE adult-only bookings paid this or less</Typography>
          </Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
            <Typography variant="caption" color="text.secondary">
              LE midpoint · median
            </Typography>
            <Typography fontWeight={700}>{aud(median)}</Typography>
            <Typography variant="caption">Half of LE adult-only bookings paid less and half paid more</Typography>
          </Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="caption" color="text.secondary">
              Upper LE benchmark · P75
            </Typography>
            <Typography fontWeight={700}>{aud(p75)}</Typography>
            <Typography variant="caption">75% of LE adult-only bookings paid this or less</Typography>
          </Box>
        </Box>
      </Box>

      {decision && (
        <Alert severity={decision.severity}>
          <Typography fontWeight={700}>{decision.title}</Typography>
          <Typography variant="body2">{decision.detail}</Typography>
        </Alert>
      )}
    </Stack>
  );
}
